/**
 * Which locale shapes this household's figures.
 *
 * Reading it straight off `navigator.language` was the obvious answer and the
 * wrong one. macOS keeps the interface language and the number format apart, so
 * a household that keeps its books in kroner and runs its browser in English
 * reports `en-US` — and every figure it has ever read as `76.221,83 kr.` would
 * restate itself as `DKK 76,221.83`: a shape it does not read, carrying a name
 * for its own money that it never uses. The reader's locale is right about the
 * reader and says nothing about the money.
 *
 * So the reader shapes the figure, as long as the reader's locale has a word of
 * its own for the currency. Where it falls back to the ISO code — `Intl`'s own
 * way of saying it has never heard of this money — the currency's home shapes
 * it instead.
 *
 * Until the household has said what its books are kept in, a figure carrying no
 * currency has nothing to agree with, and the reader is the only party present
 * to ask. Naming a currency here instead would not merely guess at one: `DKK`
 * stood here, and because kroner have a home below, it overrode the reader's own
 * grouping rather than deferring to it — so a percentage was Danish on every
 * first paint, and stayed Danish on any path that never got as far as asking.
 * The cost of deferring runs the other way: a kroner household reading in
 * English now sees `25.0 %` for as long as the settings query takes, and
 * `25,0 %` after. That trade is deliberate — a flash resolves itself, and a page
 * that never asks does not.
 */

/**
 * Only for a reader there is no way to ask: no `navigator` at all, or a browser
 * reporting no language, or one reporting a tag `Intl` will not take. None of
 * those is a browser a household reads in — they are tests and renders outside
 * one — so "this deployment's household is Danish" has no purchase here. The
 * interface's own copy is English, and the one thing this decides that is
 * language rather than punctuation is a month name.
 */
const FALLBACK_LOCALE = 'en-US'

/**
 * Only a currency that a reader's locale is likely to spell as an ISO code needs
 * an entry here: for the rest the reader always wins and this is never read.
 */
const HOME_LOCALES: Readonly<Record<string, string>> = {
  CHF: 'de-CH',
  CZK: 'cs-CZ',
  DKK: 'da-DK',
  HUF: 'hu-HU',
  ISK: 'is-IS',
  NOK: 'nb-NO',
  PLN: 'pl-PL',
  RON: 'ro-RO',
  SEK: 'sv-SE',
}

const byCurrency = new Map<string, string>()
const formatters = new Map<string, Intl.NumberFormat>()
const dateFormatters = new Map<string, Intl.DateTimeFormat>()

let household: string | undefined
let readerTag: string | undefined

/**
 * The currency the household keeps its books in, which decides the shape of
 * every figure carrying no currency of its own — a percentage, a compact axis
 * tick, a count of months. Those are read alongside the amounts, and would
 * otherwise group and punctuate differently from the figure beside them.
 */
export function adoptBaseCurrency(currency: string | undefined | null): void {
  household = normalise(currency)
}

/** The locale for a figure carrying no currency of its own. */
export function householdLocale(): string {
  return localeFor(household)
}

export function localeFor(currency: string | undefined | null): string {
  const code = normalise(currency) ?? household
  if (code === undefined) return readerLocale()

  const known = byCurrency.get(code)
  if (known !== undefined) return known

  const resolved = resolve(code)
  byCurrency.set(code, resolved)
  return resolved
}

function resolve(currency: string): string {
  const reader = readerLocale()
  const home = HOME_LOCALES[currency]
  if (home === undefined) return reader

  return namesCurrency(reader, currency) ? reader : home
}

/**
 * `Intl` spells a currency it has no name for as that currency's ISO code, so a
 * unit that came back as the code itself is the locale admitting it does not
 * know this money. It is the one signal available that needs no table of which
 * regions spend what.
 */
function namesCurrency(locale: string, currency: string): boolean {
  const unit = new Intl.NumberFormat(locale, { style: 'currency', currency })
    .formatToParts(1)
    .filter((part) => part.type === 'currency')
    .map((part) => part.value)
    .join('')
  return unit !== currency
}

/**
 * The reader's own locale, for the things that are words rather than figures.
 *
 * A currency-less figure takes the household's locale so that it groups and
 * punctuates like the amount beside it. A month name has no amount beside it and
 * nothing to agree with — it is language, and the language a household reads its
 * interface in is the reader's. So the two rules part here, and deliberately.
 */
export function readerLocale(): string {
  readerTag ??= readReader()
  return readerTag
}

function readReader(): string {
  if (typeof navigator === 'undefined') return FALLBACK_LOCALE
  const reported = navigator.language
  return reported && accepted(reported) ? reported : FALLBACK_LOCALE
}

/**
 * An ill-formed language tag reaching `Intl` throws rather than degrading, and a
 * browser that reports one should still be shown its figures. It is checked here
 * rather than where a currency is resolved because a figure carrying no currency
 * now reaches `Intl` with the reader's tag and nothing in between.
 */
function accepted(locale: string): boolean {
  try {
    new Intl.NumberFormat(locale)
    return true
  } catch {
    return false
  }
}

function normalise(currency: string | undefined | null): string | undefined {
  const code = currency?.trim().toUpperCase()
  return code ? code : undefined
}

/**
 * Cached because a holdings table builds one of these per cell, and constructing
 * the formatter costs more than the formatting it then does.
 */
export function figureFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}\u0000${JSON.stringify(options)}`
  const known = formatters.get(key)
  if (known !== undefined) return known

  const made = new Intl.NumberFormat(locale, options)
  formatters.set(key, made)
  return made
}

/** Cached for the reason figures are: a chart builds one of these per axis tick. */
export function dateFormat(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}\u0000${JSON.stringify(options)}`
  const known = dateFormatters.get(key)
  if (known !== undefined) return known

  const made = new Intl.DateTimeFormat(locale, options)
  dateFormatters.set(key, made)
  return made
}

/** Only the tests need to forget what a reader's locale already resolved to. */
export function resetFigureLocale(): void {
  household = undefined
  readerTag = undefined
  byCurrency.clear()
  formatters.clear()
  dateFormatters.clear()
}
